import React, { useState, useEffect } from 'react';
import { collection, query, getDocs } from 'firebase/firestore';
import { db } from '../firebase';

export default function OverdueReviewsModal({ serverId, serviceUserId, onClose }) {
    const [overdueItems, setOverdueItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        const fetchOverdue = async () => {
            setLoading(true);
            try {
                // Fetch Care Plans
                const cpRef = collection(db, 'servers', serverId, 'serviceUsers', serviceUserId, 'carePlans');
                const cpSnap = await getDocs(query(cpRef));
                const carePlans = cpSnap.docs.map(doc => ({ id: doc.id, type: 'Care Plan', ...doc.data() }));

                // Fetch Risk Assessments
                const raRef = collection(db, 'servers', serverId, 'serviceUsers', serviceUserId, 'riskAssessment');
                const raSnap = await getDocs(query(raRef));
                const risks = raSnap.docs.map(doc => ({ id: doc.id, type: 'Risk Assessment', ...doc.data() }));

                const now = new Date();
                
                const combined = [...carePlans, ...risks].filter(item => {
                    if (item.status === 'archived' || item.isCurrent === false) return false;
                    if (!item.reviewDate) return false;
                    
                    const reviewDate = new Date(item.reviewDate?.seconds ? item.reviewDate.seconds * 1000 : item.reviewDate);
                    return reviewDate < now;
                });
                
                combined.sort((a, b) => {
                    const dateA = new Date(a.reviewDate?.seconds ? a.reviewDate.seconds * 1000 : a.reviewDate).getTime();
                    const dateB = new Date(b.reviewDate?.seconds ? b.reviewDate.seconds * 1000 : b.reviewDate).getTime();
                    return dateA - dateB; // Oldest (most overdue) first
                });
                
                setOverdueItems(combined);
            } catch (err) {
                console.error("Error fetching overdue reviews:", err);
                setError("Failed to load overdue reviews.");
            } finally {
                setLoading(false);
            }
        };

        if (serverId && serviceUserId) {
            fetchOverdue();
        }
    }, [serverId, serviceUserId]);

    const calculateDaysOverdue = (dateString) => {
        const reviewDate = new Date(dateString?.seconds ? dateString.seconds * 1000 : dateString);
        const now = new Date();
        const diffTime = Math.abs(now - reviewDate);
        return Math.ceil(diffTime / (1000 * 60 * 60 * 24)); 
    };

    return (
        <div style={styles.modalOverlay} onClick={onClose}>
            <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
                <div style={styles.modalHeader}>
                    <div>
                        <h2 style={{ margin: 0, fontSize: '20px', color: '#111827' }}>Overdue Reviews</h2>
                        <p style={{ margin: '4px 0 0', color: '#6b7280', fontSize: '14px' }}>Active Care Plans and Risk Assessments that require review</p>
                    </div>
                    <button onClick={onClose} style={styles.closeBtn}>×</button>
                </div>

                <div style={styles.scrollArea}>
                    {loading ? (
                        <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>Loading overdue reviews...</div>
                    ) : error ? (
                        <div style={{ padding: '40px', textAlign: 'center', color: '#ef4444' }}>{error}</div>
                    ) : overdueItems.length === 0 ? (
                        <div style={styles.emptyState}>
                            <div style={{ fontSize: '48px', marginBottom: '16px' }}>🎉</div>
                            <h3 style={{ margin: '0 0 8px 0', color: '#374151' }}>Up to Date</h3>
                            <p style={{ margin: 0, color: '#6b7280' }}>There are no overdue reviews for this service user.</p>
                        </div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                            {overdueItems.map((item, index) => {
                                const title = item.title || item.carePlanTitle || item.riskType || item.name || `Untitled ${item.type}`;
                                const daysOverdue = calculateDaysOverdue(item.reviewDate);
                                
                                return (
                                    <div key={item.id || index} style={styles.recordCard}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                                            <div>
                                                <h3 style={{ margin: 0, fontSize: '16px', color: '#111827' }}>{title}</h3>
                                                <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '4px' }}>
                                                    {item.type}
                                                </div>
                                            </div>
                                            <span style={{ 
                                                display: 'inline-block', padding: '4px 10px', borderRadius: '9999px', fontSize: '11px', fontWeight: 'bold',
                                                backgroundColor: '#fee2e2', color: '#991b1b', border: '1px solid #fecaca'
                                            }}>
                                                {daysOverdue} DAYS OVERDUE
                                            </span>
                                        </div>
                                        
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', borderTop: '1px solid #f3f4f6', paddingTop: '16px' }}>
                                            <div style={{ flex: '1', minWidth: '150px' }}>
                                                <label style={styles.fieldLabel}>Expected Review Date</label>
                                                <div style={styles.fieldValue}>{new Date(item.reviewDate?.seconds ? item.reviewDate.seconds * 1000 : item.reviewDate).toLocaleDateString()}</div>
                                            </div>
                                            <div style={{ flex: '1', minWidth: '150px' }}>
                                                <label style={styles.fieldLabel}>Author</label>
                                                <div style={styles.fieldValue}>{item.author || item.createdBy || 'Unknown'}</div>
                                            </div>
                                        </div>
                                        
                                        <div style={{ marginTop: '16px' }}>
                                            <button style={styles.viewBtn} onClick={() => alert(`Review action: Coming to React V2 soon.`)}>
                                                Conduct Review
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

const styles = {
    modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 },
    modalContent: { backgroundColor: 'white', borderRadius: '12px', width: '90%', maxWidth: '700px', maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' },
    modalHeader: { padding: '24px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' },
    closeBtn: { background: 'none', border: 'none', fontSize: '24px', color: '#9ca3af', cursor: 'pointer', lineHeight: 1 },
    scrollArea: { padding: '24px', overflowY: 'auto', flex: 1, backgroundColor: '#f9fafb' },
    emptyState: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px 0', textAlign: 'center', backgroundColor: 'white', borderRadius: '8px', border: '1px dashed #d1d5db' },
    recordCard: { backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '20px', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' },
    fieldLabel: { display: 'block', fontSize: '11px', textTransform: 'uppercase', color: '#6b7280', marginBottom: '4px', fontWeight: 'bold' },
    fieldValue: { fontSize: '14px', color: '#111827' },
    viewBtn: { width: '100%', padding: '10px', backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '6px', color: '#991b1b', fontSize: '14px', fontWeight: 'bold', cursor: 'pointer', transition: 'background-color 0.2s' }
};
